/**
 * Environment Variable Adapter
 *
 * Provides a unified interface for accessing environment variables across platforms.
 * - Web (Vite): Uses import.meta.env.VITE_*
 * - React Native: Uses react-native-config or inline config
 *
 * @example
 * import { setEnvAdapter, getEnv } from '@prosperia/shared';
 *
 * // Web setup
 * setEnvAdapter({
 *   SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
 *   SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
 *   // ...
 * });
 *
 * // React Native setup
 * import Config from 'react-native-config';
 * setEnvAdapter(Config);
 */

// Default environment configuration
let envConfig = {};

/**
 * Set the environment configuration
 * Call this at app initialization before accessing environment variables
 *
 * @param {Object} config - Object containing environment variables
 */
export function setEnvAdapter(config) {
  envConfig = { ...config };
}

/**
 * Get an environment variable
 *
 * @param {string} key - The environment variable key (without prefix)
 * @returns {string|undefined} The value or undefined if not set
 */
export function getEnv(key) {
  return envConfig[key];
}

/**
 * Get a required environment variable (throws if not set)
 *
 * @param {string} key - The environment variable key
 * @returns {string} The value
 * @throws {Error} If the variable is not set
 */
export function getRequiredEnv(key) {
  const value = envConfig[key];
  if (value === undefined || value === null || value === '') {
    throw new Error(`Required environment variable "${key}" is not set`);
  }
  return value;
}

/**
 * Check if an environment variable is set
 *
 * @param {string} key - The environment variable key
 * @returns {boolean} True if the variable is set and not empty
 */
export function hasEnv(key) {
  const value = envConfig[key];
  return value !== undefined && value !== null && value !== '';
}

/**
 * Get all environment variables (for debugging)
 *
 * @returns {Object} All configured environment variables (keys only, not values)
 */
export function getEnvKeys() {
  return Object.keys(envConfig);
}

// Common environment variable keys used in the app
export const ENV_KEYS = {
  // Supabase
  SUPABASE_URL: 'SUPABASE_URL',
  SUPABASE_ANON_KEY: 'SUPABASE_ANON_KEY',

  // Stripe
  STRIPE_PUBLISHABLE_KEY: 'STRIPE_PUBLISHABLE_KEY',

  // Firebase
  FIREBASE_API_KEY: 'FIREBASE_API_KEY',
  FIREBASE_AUTH_DOMAIN: 'FIREBASE_AUTH_DOMAIN',
  FIREBASE_PROJECT_ID: 'FIREBASE_PROJECT_ID',
  FIREBASE_STORAGE_BUCKET: 'FIREBASE_STORAGE_BUCKET',
  FIREBASE_MESSAGING_SENDER_ID: 'FIREBASE_MESSAGING_SENDER_ID',
  FIREBASE_APP_ID: 'FIREBASE_APP_ID',
  FIREBASE_VAPID_KEY: 'FIREBASE_VAPID_KEY',

  // Google Maps
  GOOGLE_MAPS_API_KEY: 'GOOGLE_MAPS_API_KEY',
};

export default {
  setEnvAdapter,
  getEnv,
  getRequiredEnv,
  hasEnv,
  getEnvKeys,
  ENV_KEYS,
};
